import {
  type PropsWithChildren,
  forwardRef,
  useRef,
  useState,
  useEffect,
} from "react";

import ThreeDotsIcon from "$/lib/icons/ThreeDotsIcon";
import { useOnClickOutside } from "usehooks-ts";
import { IconButton } from "../../atoms";
import Flexbox from "../../common/Flexbox";
import type { TableBasicMenuProps } from "./TableBasicMenu.types";
import { cn } from "$/lib/utils/functions/misc.functions";

const TableBasicMenu = forwardRef<
  HTMLDivElement,
  PropsWithChildren<TableBasicMenuProps>
>(({ items }, forwardedRef) => {
  const [isOpen, setIsOpen] = useState(false);
  const [placement, setPlacement] = useState<"top" | "bottom">("bottom");

  const menuRef = useRef<HTMLUListElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const toggleMenu = () => {
    setIsOpen((prev) => !prev);
  };

  const closeMenu = () => {
    setIsOpen(false);
  };

  const handleClickOutside = (event: Event) => {
    if (
      menuRef.current &&
      !menuRef.current.contains(event.target as Node) &&
      buttonRef.current &&
      !buttonRef.current.contains(event.target as Node)
    )
      closeMenu();
  };

  const updateMenuPlacement = () => {
    if (buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const viewportHeight = window.innerHeight;

      if (buttonRect.bottom + 150 > viewportHeight) setPlacement("top");
      else setPlacement("bottom");
    }
  };

  useOnClickOutside(menuRef, handleClickOutside);

  useEffect(() => {
    if (isOpen) {
      updateMenuPlacement();

      const handleScroll = () => updateMenuPlacement();

      window.addEventListener("scroll", handleScroll);
      window.addEventListener("resize", handleScroll);

      return () => {
        window.removeEventListener("scroll", handleScroll);
        window.removeEventListener("resize", handleScroll);
      };
    }
  }, [isOpen]);

  return (
    <div ref={forwardedRef} className="relative inline-block">
      <IconButton
        ref={buttonRef}
        onClick={toggleMenu}
        icon={<ThreeDotsIcon />}
        className="bg-primary-100 text-secondary"
      />
      {isOpen && (
        <ul
          ref={menuRef}
          className={cn(
            "absolute right-0 z-10 flex w-fit min-w-max flex-col rounded-xl border border-gray-200 bg-white shadow-lg",
            {
              "top-full mt-2": placement === "bottom",
              "bottom-full mb-2": placement === "top",
            },
          )}
        >
          {items.map((item) => (
            <li
              key={item.label}
              onClick={item.onClick}
              className="cursor-pointer p-3 hover:bg-gray-100"
            >
              <Flexbox row align="center" className="gap-2">
                {item.icon}
                <p>{item.label}</p>
              </Flexbox>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});

TableBasicMenu.displayName = "TableBasicMenu";

export default TableBasicMenu;
